import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, Download, PanelRightClose } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { buildVirtualBundleZipEntries, downloadAssetBundleZip } from "../../../lib/assetBundleZip";
import { titleCaseZipName } from "../../../lib/chatZipApi";
import ChatZipArchiveExplorer from "./ChatZipArchiveExplorer";

export default function ChatZipBundleArtifactPane({
  assetBundle,
  pdfAttachment,
  uploadedAssets,
  onOpenPdfAttachment,
  className,
  overlayMode = false,
  onClose,
}) {
  const [selectedPath, setSelectedPath] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const entries = useMemo(
    () =>
      buildVirtualBundleZipEntries({
        bundle: assetBundle,
        pdfAttachment,
        uploadedAssets,
      }),
    [assetBundle, pdfAttachment, uploadedAssets]
  );

  const filename = titleCaseZipName(assetBundle?.fileName || assetBundle?.title || "Campaign-Assets.zip");

  useEffect(() => {
    const first = entries[0]?.path || null;
    setSelectedPath(first);
  }, [entries]);

  const totalFiles = entries.length;
  const subtitle =
    totalFiles === 1 ? "1 file in archive" : `${totalFiles} files in archive`;

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadAssetBundleZip({
        bundle: assetBundle,
        pdfAttachment,
        uploadedAssets,
      });
    } catch (err) {
      console.error("[ChatZipBundleArtifactPane] ZIP download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [assetBundle, pdfAttachment, uploadedAssets, downloading]);

  const resolvePdfAttachmentForEntry = useCallback(
    (entry) =>
      entry?.bundledAssistantPdf && pdfAttachment?.data ? pdfAttachment : null,
    [pdfAttachment]
  );

  if (!assetBundle || !entries.length) {
    return (
      <div
        className={cn(
          "flex h-full min-h-0 w-full flex-col items-center justify-center gap-2 bg-muted/20 px-4 text-center text-sm text-muted-foreground",
          className
        )}
      >
        <Archive className="h-10 w-10 opacity-40" aria-hidden />
        <p>ZIP preview unavailable.</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full min-h-0 w-full flex-col overflow-hidden rounded-none border-l border-border/80 bg-[hsl(220,14%,96%)] shadow-[inset_1px_0_0_hsl(220,13%,91%)] dark:border-border dark:bg-[hsl(240,6%,10%)] dark:shadow-[inset_1px_0_0_hsl(240,4%,18%)]",
        className
      )}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border/70 bg-background/85 px-3 py-2.5 backdrop-blur-md dark:bg-[hsl(240,5%,12%)]/90">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
          <span className="hidden shrink-0 rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:inline">
            ZIP
          </span>
          <div className="flex min-w-0 items-center gap-2">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#E85D3F]/12 text-[#B9422B] dark:bg-[#E85D3F]/18 dark:text-[#ff9a83]"
            >
              <Archive className="h-4 w-4" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0">
              <span className="block truncate text-sm font-medium text-foreground" title={filename}>
                {filename}
              </span>
              <span className="block truncate text-[11px] text-muted-foreground">{subtitle}</span>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="relative h-8 w-8"
            disabled={downloading}
            aria-busy={downloading}
            onClick={handleDownload}
            aria-label={downloading ? "Downloading ZIP" : "Download ZIP"}
          >
            <span className={cn("inline-flex", downloading && "invisible")}>
              <Download className="h-4 w-4" aria-hidden />
            </span>
            {downloading ? (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <LoadingSpinner />
              </span>
            ) : null}
          </Button>
          {overlayMode && typeof onClose === "function" ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-orange-500 hover:bg-orange-500/10 hover:text-orange-600"
              onClick={onClose}
              aria-label="Close preview"
            >
              <PanelRightClose className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </header>

      <ChatZipArchiveExplorer
        className="min-h-0 flex-1 bg-[hsl(220,14%,94%)]/90 dark:bg-black/25"
        entries={entries}
        selectedPath={selectedPath}
        onSelectPath={setSelectedPath}
        resolvePdfAttachmentForEntry={resolvePdfAttachmentForEntry}
        onOpenPdfAttachment={onOpenPdfAttachment}
      />
    </div>
  );
}
